/**
 * /:projectCode/tickets/:shorthand — full-page card view for a ticket.
 *
 * Legacy parity URL shape (3mote-fe `src/router.jsx`:135). Same
 * resolution pattern as the tasks deep-link route — projectCode +
 * shorthand resolved via the dashboard cache, rendered inline so the
 * AppShell sidebar stays visible.
 */

import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect } from 'react'

import { getAccessToken } from '@/auth/cookie-helpers'
import { CardDetailPage, type CardPageSearch } from '@/features/board/components/CardDetailPage'

export const Route = createFileRoute('/$projectCode/tickets/$shorthand')({
  component: TicketDeepLinkRoute,
  validateSearch: (search: Record<string, unknown>): CardPageSearch => ({
    commentId: typeof search.commentId === 'string' ? search.commentId : undefined,
    parentCommentId:
      typeof search.parentCommentId === 'string' ? search.parentCommentId : undefined,
  }),
})

function TicketDeepLinkRoute() {
  const navigate = Route.useNavigate()
  const { commentId, parentCommentId } = Route.useSearch()
  const stripCommentId = useCallback(
    () =>
      navigate({
        search: (prev) => ({ ...prev, commentId: undefined, parentCommentId: undefined }),
        replace: true,
      }),
    [navigate],
  )
  const { projectCode, shorthand } = Route.useParams()

  useEffect(() => {
    if (!getAccessToken()) {
      navigate({ to: '/login' })
    }
  }, [navigate])

  if (!getAccessToken()) return null

  return (
    <CardDetailPage
      kind="ticket"
      projectCode={projectCode}
      shorthand={shorthand}
      commentId={commentId}
      parentCommentId={parentCommentId}
      onCommentIdConsumed={stripCommentId}
    />
  )
}
